/**
 * RequireCompanyAdmin.jsx - Guard for B2B-administratorer
 * 
 * Sjekker at brukeren:
 * 1. Er innlogget
 * 2. Har rolle company_admin
 * 
 * Hvis bruker er company_user men ikke admin -> redirect til /company/dashboard
 * Hvis bruker er privat -> redirect til /chat
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Loading spinner
const LoadingSpinner = () => (
  <div className="min-h-screen bg-[#0F1419] flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-2 border-[#B87333]/30 border-t-[#B87333] rounded-full animate-spin" />
      <span className="text-[#8899A8] text-sm">Laster...</span>
    </div>
  </div>
);

export default function RequireCompanyAdmin({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  // Ikke innlogget
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Sjekk om brukeren har B2B-tilgang
  const isCompanyUser = 
    user.account_type === 'business' ||
    user.company_role === 'company_user' ||
    user.company_role === 'company_admin';

  if (!isCompanyUser) {
    // Privat bruker - send til chat
    return <Navigate to="/chat" replace />;
  }

  // Sjekk om brukeren er admin
  const isAdmin = 
    user.company_role === 'company_admin' ||
    user.role === 'company_admin' ||
    // Fallback: Alle business-brukere uten spesifikk rolle er admin
    (user.account_type === 'business' && !user.company_role);

  if (!isAdmin) {
    // company_user uten admin-tilgang -> redirect til dashboard
    return <Navigate to="/company/dashboard" replace />;
  }

  return children;
}
